import { useState, type ChangeEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { requestsApi } from '../features/requests/api.ts';
import { downloadFile } from '../features/documents/api.ts';
import { fileToBase64, UPLOAD_ACCEPT } from '../lib/upload.ts';
import { useAuthStore } from '../store/auth.ts';
import { canProcure } from '../lib/roles.ts';
import { t } from '../lib/strings.ts';

/**
 * Optional single specification document on a request. Procurement staff can
 * upload / replace / remove it while the request is open; everyone sees a
 * download link. Vendors get their own copy via the public quote page.
 */
export function SpecDocBox({ requestId }: { requestId: string }): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const canAct = canProcure(role);
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const { data: specDoc, isLoading } = useQuery({
    queryKey: ['specDoc', requestId],
    queryFn: () => requestsApi.getSpecDoc(requestId),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['specDoc', requestId] });

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const { dataBase64, mime } = await fileToBase64(file);
      return requestsApi.uploadSpecDoc(requestId, { fileName: file.name, mime, dataBase64 });
    },
    onSuccess: invalidate,
    onError: () => setErr(t.specDoc.uploadFailed),
  });

  const deleteMut = useMutation({
    mutationFn: () => requestsApi.deleteSpecDoc(requestId),
    onSuccess: invalidate,
    onError: () => setErr(t.specDoc.deleteFailed),
  });

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    setErr(null);
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) uploadMut.mutate(file);
  }

  async function handleDownload() {
    if (specDoc) await downloadFile(requestsApi.specDocFileUrl(requestId), specDoc.fileName);
  }

  if (isLoading) return <></>;
  // Nothing to show to non-procurement viewers when there's no doc.
  if (!specDoc && !canAct) return <></>;

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="status-label text-muted-foreground">{t.specDoc.label}</span>
          {!specDoc && <p className="mt-0.5 text-xs text-muted-foreground">{t.specDoc.hint}</p>}
        </div>
        {canAct && (
          <label className="btn-secondary btn-sm cursor-pointer gap-1">
            <Icon name="upload" size={14} />
            {uploadMut.isPending ? t.specDoc.uploading : specDoc ? t.specDoc.replace : t.specDoc.add}
            <input type="file" accept={UPLOAD_ACCEPT} className="hidden" onChange={onPick} disabled={uploadMut.isPending} />
          </label>
        )}
      </div>

      {specDoc && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <button type="button" onClick={() => void handleDownload()} className="flex min-w-0 items-center gap-1.5 text-sm text-primary hover:underline">
            <Icon name="description" size={14} className="shrink-0" />
            <span className="truncate" dir="ltr">{specDoc.fileName}</span>
            <Icon name="download" size={14} className="shrink-0" />
          </button>
          {canAct && (
            <button type="button" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending} className="btn-ghost btn-sm text-destructive" aria-label={t.specDoc.remove}>
              <Icon name="close" size={14} />
            </button>
          )}
        </div>
      )}

      {err && <p className="mt-1 text-xs text-destructive">{err}</p>}
    </div>
  );
}
